import { onBeforeUnmount, onMounted, type Ref } from 'vue';

/**
 * Drive horizontal scrolling for markdown tables emitted by
 * {@link useAssistantMessageMarkdown}'s `table_open`/`table_close` rules.
 *
 * Each table is rendered as:
 *
 *   .vision-table-pan
 *     .vision-table-scroll   (overflow-x-auto, hidden by default)
 *       .vision-table-spacer (width = table.scrollWidth)
 *     .vision-table-track    (overflow-x-clip overflow-y-visible)
 *       <table>
 *
 * The proxy `.vision-table-scroll` provides a real native scrollbar over a
 * spacer that matches the table's intrinsic width. We translate the track
 * horizontally to mirror the proxy's scrollLeft. The track's `overflow-x: clip`
 * + `overflow-y: visible` is the one combination that clips horizontal
 * overflow without establishing a sticky containing block, so `<th>` cells
 * inside the table can still stick to the SidePanel scroll.
 */
export function useMarkdownTableScrollbars(hostRef: Ref<HTMLElement | null>) {
  const wired = new Map<HTMLElement, () => void>();
  let mutationObserver: MutationObserver | null = null;

  function setupPan(pan: HTMLElement) {
    if (wired.has(pan)) return;

    const track = pan.querySelector<HTMLElement>(':scope > .vision-table-track');
    const proxy = pan.querySelector<HTMLElement>(':scope > .vision-table-scroll');
    const spacer = proxy?.querySelector<HTMLElement>(':scope > .vision-table-spacer');
    const table = track?.querySelector<HTMLTableElement>(':scope > table');
    if (!track || !proxy || !spacer || !table) return;

    function sync() {
      const tableWidth = table!.scrollWidth;
      const panWidth = pan.clientWidth;
      const overflows = tableWidth > panWidth + 1;
      spacer!.style.width = `${tableWidth}px`;
      proxy!.classList.toggle('hidden', !overflows);
      if (!overflows) {
        table!.style.transform = '';
        proxy!.scrollLeft = 0;
      }
    }

    function onProxyScroll() {
      // Translate the <table>, not the .vision-table-track. With overflow-x:
      // clip on the track, the clip rect is fixed in the track's box; moving
      // the table within that fixed clip is what actually pans the visible
      // columns. Translating the track instead moves the clip rect along with
      // it, so the user sees the same slice nudged off-screen.
      table!.style.transform = `translateX(-${proxy!.scrollLeft}px)`;
    }

    function onTrackWheel(event: WheelEvent) {
      // overflow-x: clip refuses native scroll input, so trackpad two-finger
      // horizontal swipes (deltaX) and shift+wheel are dead. Forward them to
      // the proxy. Only consume the event when we can actually scroll further
      // — otherwise let it bubble so the SidePanel can handle vertical scroll.
      if (event.deltaX === 0) return;
      const max = proxy!.scrollWidth - proxy!.clientWidth;
      const next = Math.max(0, Math.min(max, proxy!.scrollLeft + event.deltaX));
      if (next === proxy!.scrollLeft) return;
      event.preventDefault();
      proxy!.scrollLeft = next;
    }

    proxy.addEventListener('scroll', onProxyScroll, { passive: true });
    track.addEventListener('wheel', onTrackWheel, { passive: false });
    const ro = new ResizeObserver(sync);
    ro.observe(table);
    ro.observe(pan);
    sync();

    wired.set(pan, () => {
      proxy.removeEventListener('scroll', onProxyScroll);
      track.removeEventListener('wheel', onTrackWheel);
      ro.disconnect();
    });
  }

  function refresh() {
    const host = hostRef.value;
    if (!host) return;

    const live = new Set(host.querySelectorAll<HTMLElement>('.vision-table-pan'));
    for (const [pan, cleanup] of wired) {
      if (!live.has(pan)) {
        cleanup();
        wired.delete(pan);
      }
    }
    live.forEach(setupPan);
  }

  onMounted(() => {
    refresh();
    const host = hostRef.value;
    if (!host) return;
    mutationObserver = new MutationObserver(refresh);
    mutationObserver.observe(host, { childList: true, subtree: true });
  });

  onBeforeUnmount(() => {
    mutationObserver?.disconnect();
    mutationObserver = null;
    for (const cleanup of wired.values()) cleanup();
    wired.clear();
  });
}
